/**
 * Pure frame-model logic (Block B) — no @excalidraw runtime imports, only types,
 * so it is unit-testable in isolation. The imperative helpers that actually
 * mutate the scene (create/insert/update via the Excalidraw API) live in
 * scene-links.ts and re-export from here.
 *
 * frame_type = "sectional" | "image" | "embed" (customData.ryfeFrameType); a
 * frame with no/unknown type is `sectional`. MAIN is NOT a frame type — it is a
 * scene-level pointer stored on the file (files.main_frame_id).
 */

import type { ExcalidrawElement } from "@excalidraw/element/types";

export const SCENE_LINK_KEY = "ryfeSceneLink";
export const FRAME_TYPE_KEY = "ryfeFrameType";
export const EMBED_TARGET_KEY = "ryfeEmbedTarget";
// CC_06 (F1/F2/F4) — orthogonal frame flags in the RYFE customData namespace.
// A frame carries the id of its image scene-asset (Bilderrahmen) and the fit
// mode; the preview-anchor stays the file-level `main_frame_id` pointer (F3).
export const FRAME_IMAGE_KEY = "ryfeImageFileId";
export const IMAGE_FIT_KEY = "ryfeImageFit";
// CC_10 Block 1 (D-54) — the in-scene display mode of an embedded/previewed
// element, read ONLY by the host-render wrapper (never by packages/excalidraw,
// D-04). Namespaced like every other RYFE flag.
export const DISPLAY_MODE_KEY = "ryfeDisplayMode";

export type FrameType = "sectional" | "image" | "embed";
export type ImageFit = "original" | "fit" | "fill";
export type SceneLinkMode = "button" | "preview";
/**
 * CC_10 Block 1 (D-54) — how a host-rendered embed/preview scales inside its box:
 *   • scaleWithCanvas (Default) — the preview zooms together with the host canvas
 *     (today's behaviour): the inner scene shares the outer zoom.
 *   • keepReadable — the inner scene is rendered at a readable base scale and does
 *     NOT shrink below it as the host zooms out (text stays legible).
 *   • noScale — the inner scene renders at 1:1, independent of the host zoom.
 * Overflow is CLIPPED in every mode (conservative; scroll is a later opt-in).
 */
export type DisplayMode = "scaleWithCanvas" | "keepReadable" | "noScale";

/**
 * CC_06 Block 1 (F2) — the ORTHOGONAL state of a frame. Unlike `getFrameType`
 * (one visual type), these flags are independent: a frame can be a Bilderrahmen
 * (image) AND the preview-anchor at once (Mockup v2, card 4). `sectional` means
 * none of link/image is set. The anchor is orthogonal and never changes the
 * sectional/image distinction.
 */
export interface FrameState {
  link: boolean;
  image: boolean;
  previewAnchor: boolean;
  sectional: boolean;
}

export interface SceneLinkData {
  targetFileId: string;
  targetName?: string;
  mode: SceneLinkMode;
}
export interface EmbedTarget {
  targetFileId: string;
  targetName?: string;
}
export interface PreviewLink {
  element: ExcalidrawElement;
  data: SceneLinkData;
}

type ElementWithMeta = ExcalidrawElement & {
  customData?: Record<string, unknown>;
  link?: string | null;
};

export const boardPath = (fileId: string): string => `/board/${fileId}`;

/** Parse an in-app board path back to a file id, or null if it isn't one. */
export const parseBoardLink = (
  link: string | null | undefined,
): string | null => {
  if (!link) {
    return null;
  }
  const m = /(?:^|\/)board\/([^/?#]+)/.exec(link);
  return m ? m[1] : null;
};

export const isFrameEl = (el: ExcalidrawElement): boolean =>
  el.type === "frame" || el.type === "magicframe";

export type ElementLinkInfo =
  | { kind: "none" }
  | { kind: "external"; url: string }
  | { kind: "internal"; targetFileId: string; targetName?: string };

/**
 * Describe the link on an element (CC_05 Block 6). An internal link is an in-app
 * board path (`/board/:id`) — the click handler then routes by the target's
 * KIND (Block 1), so this is kind-agnostic here. Everything else is external.
 */
export const getElementLinkInfo = (el: ExcalidrawElement): ElementLinkInfo => {
  const link = (el as ElementWithMeta).link;
  if (!link) {
    return { kind: "none" };
  }
  const targetFileId = parseBoardLink(link);
  if (targetFileId) {
    const meta = (el as ElementWithMeta).customData?.[SCENE_LINK_KEY] as
      | SceneLinkData
      | undefined;
    return { kind: "internal", targetFileId, targetName: meta?.targetName };
  }
  return { kind: "external", url: link };
};

/**
 * R4 (Punkt 15) — how an internal element link presents itself in the scene:
 * as a click-button or as an inline document preview. Missing/unknown → button
 * (the creation default).
 */
export const getSceneLinkMode = (el: ExcalidrawElement): SceneLinkMode => {
  const meta = (el as ElementWithMeta).customData?.[SCENE_LINK_KEY] as
    | SceneLinkData
    | undefined;
  return meta?.mode === "preview" ? "preview" : "button";
};

/**
 * The RYFE frame type of an element (Block B.1). A frame with no/unknown type is
 * `sectional`; legacy values are mapped (default→sectional, main→sectional,
 * linked→embed).
 */
export const getFrameType = (el: ExcalidrawElement): FrameType => {
  const raw = (el as ElementWithMeta).customData?.[FRAME_TYPE_KEY];
  switch (raw) {
    case "image":
      return "image";
    case "embed":
    case "linked": // legacy
      return "embed";
    case "sectional":
    case "default": // legacy
    case "main": // legacy anchor marking — the type itself is sectional
    default:
      return "sectional";
  }
};

/** The embed target of an embed frame, or null. */
export const getEmbedTarget = (el: ExcalidrawElement): EmbedTarget | null => {
  const t = (el as ElementWithMeta).customData?.[EMBED_TARGET_KEY] as
    | EmbedTarget
    | undefined;
  return t && t.targetFileId ? t : null;
};

/** The image scene-asset id a frame/element carries (Bilderrahmen), or null. */
export const getFrameImageId = (el: ExcalidrawElement): string | null => {
  const id = (el as ElementWithMeta).customData?.[FRAME_IMAGE_KEY];
  return typeof id === "string" && id ? id : null;
};

/** The image fit mode (F4), defaulting to "fit". */
export const getImageFit = (el: ExcalidrawElement): ImageFit => {
  const f = (el as ElementWithMeta).customData?.[IMAGE_FIT_KEY];
  return f === "original" || f === "fill" ? f : "fit";
};

/**
 * CC_10 Block 1 (D-54) — the in-scene display mode of an element, defaulting to
 * "scaleWithCanvas" (today's behaviour). Any missing/unknown value maps to the
 * default, so existing scenes keep rendering exactly as before.
 */
export const getDisplayMode = (el: ExcalidrawElement): DisplayMode => {
  const m = (el as ElementWithMeta).customData?.[DISPLAY_MODE_KEY];
  return m === "keepReadable" || m === "noScale" ? m : "scaleWithCanvas";
};

/**
 * CC_10 Block 1 — the effective render scale for a preview, given the host
 * canvas zoom. Pure so the three modes are unit-tested without a DOM:
 *   • scaleWithCanvas → follows the host zoom 1:1.
 *   • keepReadable    → never below `minReadable` (default 1), else the host zoom.
 *   • noScale         → always 1, independent of the host zoom.
 */
export const displayModeScale = (
  mode: DisplayMode,
  hostZoom: number,
  minReadable = 1,
): number => {
  const z = hostZoom > 0 ? hostZoom : 1;
  switch (mode) {
    case "noScale":
      return 1;
    case "keepReadable":
      return Math.max(z, minReadable);
    case "scaleWithCanvas":
    default:
      return z;
  }
};

/**
 * CC_06 Block 1 (F2) — the orthogonal state of a frame. `mainFrameId` is the
 * file-level preview-anchor pointer (F3); pass it so `previewAnchor` reflects it.
 */
export const getFrameState = (
  el: ExcalidrawElement,
  mainFrameId?: string | null,
): FrameState => {
  const link =
    !!getEmbedTarget(el) ||
    parseBoardLink((el as ElementWithMeta).link) !== null;
  const image = getFrameImageId(el) !== null;
  const previewAnchor = !!mainFrameId && el.id === mainFrameId;
  return { link, image, previewAnchor, sectional: !link && !image };
};

interface Box {
  x: number;
  y: number;
  width: number;
  height: number;
}

/**
 * CC_06 Block 3 (F4) — geometry of a framed image for a fit mode. Pure, so the
 * three modes are unit-tested. `original` keeps the natural size; `fit` scales
 * to fully contain the image inside the frame; `fill` scales to cover it (the
 * overflow is clipped by the frame). All three centre the image in the frame.
 */
export const computeImageBox = (
  frame: Box,
  natural: { width: number; height: number },
  fit: ImageFit,
): Box => {
  const nw = Math.max(1, natural.width);
  const nh = Math.max(1, natural.height);
  let w: number;
  let h: number;
  if (fit === "original") {
    w = nw;
    h = nh;
  } else {
    const scale =
      fit === "fill"
        ? Math.max(frame.width / nw, frame.height / nh)
        : Math.min(frame.width / nw, frame.height / nh);
    w = nw * scale;
    h = nh * scale;
  }
  return {
    x: frame.x + (frame.width - w) / 2,
    y: frame.y + (frame.height - h) / 2,
    width: w,
    height: h,
  };
};

/**
 * Legacy main-frame ids (Block B.4): frames that carry the OLD in-scene "main"
 * marking (customData.ryfeFrameType === "main"), in scene order (≈ creation
 * order). Used once on load to seed files.main_frame_id when it is still empty.
 */
export const legacyMainFrameIds = (
  elements: readonly ExcalidrawElement[],
): string[] =>
  elements
    .filter(
      (el) =>
        isFrameEl(el) &&
        !el.isDeleted &&
        (el as ElementWithMeta).customData?.[FRAME_TYPE_KEY] === "main",
    )
    .map((el) => el.id);

/**
 * Resolve the main frame of a scene from the pointer (Block B.2/B.3.3). Returns
 * the element only if the pointer references an existing, non-deleted, non-embed
 * frame; otherwise null → the caller renders the whole scene (the fallback).
 */
export const resolveMainFrame = (
  elements: readonly ExcalidrawElement[],
  mainFrameId: string | null | undefined,
): ExcalidrawElement | null => {
  if (!mainFrameId) {
    return null;
  }
  const el = elements.find((e) => e.id === mainFrameId && !e.isDeleted);
  if (!el || !isFrameEl(el) || getFrameType(el) === "embed") {
    return null;
  }
  return el;
};

/**
 * Auto-assignment of the main anchor (Block B.3.2). If the scene already has a
 * (valid) pointer, it is kept. Otherwise the FIRST `sectional` frame becomes the
 * main frame; `image` and `embed` frames never trigger auto-assignment. Returns
 * the id to store, or null for "leave empty → whole scene".
 */
export const pickAutoMainFrameId = (
  elements: readonly ExcalidrawElement[],
  currentMainFrameId: string | null | undefined,
): string | null => {
  if (resolveMainFrame(elements, currentMainFrameId)) {
    return currentMainFrameId as string;
  }
  const first = elements.find(
    (el) => !el.isDeleted && isFrameEl(el) && getFrameType(el) === "sectional",
  );
  return first ? first.id : null;
};

/**
 * All non-deleted elements that request an inline preview of another scene:
 * scene-link elements with mode "preview" AND embed frames that carry a target.
 * Both render through the same guarded content endpoint (no bypass).
 */
export const readPreviewLinks = (
  elements: readonly ExcalidrawElement[],
): PreviewLink[] => {
  const out: PreviewLink[] = [];
  for (const el of elements) {
    if (el.isDeleted) {
      continue;
    }
    const link = (el as ElementWithMeta).customData?.[SCENE_LINK_KEY] as
      | SceneLinkData
      | undefined;
    if (link && link.mode === "preview" && link.targetFileId) {
      out.push({ element: el, data: link });
      continue;
    }
    if (isFrameEl(el) && getFrameType(el) === "embed") {
      const t = getEmbedTarget(el);
      if (t) {
        out.push({
          element: el,
          data: {
            targetFileId: t.targetFileId,
            targetName: t.targetName,
            mode: "preview",
          },
        });
      }
    }
  }
  return out;
};
